import { useEffect, useMemo, useState } from "react";
import { useLocalization } from "../../../hooks/useLocalization";
import { fetchLeaderboard, fetchLeaderboardHistory } from "../../../services/leaderboardServices";
import { getErrorMessage } from "../../../services/utils/apiError";
import { LeaderboardTeam } from "../../../types/leaderboard";
import { buildChartData } from "../../../utils/leaderboard/chart-data";
import { normalizeLeaderboardTeams } from "../../../utils/leaderboard/leaderboard-data";
import { LEADERBOARD_POLL_MS } from "../../../utils/polling";
import { buildTeamMetaById } from "../DailyMovers";

export async function loadLeaderboardData() {
  const [snapshot, history] = await Promise.all([fetchLeaderboard(), fetchLeaderboardHistory(100)]);

  const snapshotTeams = snapshot?.teams ?? [];
  const chart = buildChartData(history, {
    teamIds: new Set(snapshotTeams.map((team) => team.teamId)),
    teamLabels: new Map(snapshotTeams.map((team) => [team.teamId, team.fullName || team.teamName])),
  });

  return {
    teams: normalizeLeaderboardTeams(snapshotTeams),
    lastUpdated: snapshot?.createdAt ?? null,
    live: snapshot?.live ?? true,
    chartData: chart.data,
    chartTeams: chart.teams,
  };
}

export const useLeaderboardData = () => {
  const { strings } = useLocalization();

  const [teams, setTeams] = useState<LeaderboardTeam[]>([]);
  const [standingsLoading, setStandingsLoading] = useState(true);
  const [chartData, setChartData] = useState<ReturnType<typeof buildChartData>["data"]>([]);
  const [chartTeams, setChartTeams] = useState<string[]>([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [live, setLive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const refresh = async (showLoading: boolean) => {
      if (showLoading) {
        setStandingsLoading(true);
        setChartLoading(true);
      }
      try {
        const data = await loadLeaderboardData();
        if (cancelled) return;
        setTeams(data.teams);
        setLastUpdated(data.lastUpdated);
        setLive(data.live);
        setChartData(data.chartData);
        setChartTeams(data.chartTeams);
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setError(getErrorMessage(err, strings.LEADERBOARD.ERROR_LOAD));
        }
      } finally {
        if (!cancelled) {
          setStandingsLoading(false);
          setChartLoading(false);
        }
      }
    };

    void refresh(true);
    const timer = window.setInterval(() => {
      void refresh(false);
    }, LEADERBOARD_POLL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [strings.LEADERBOARD.ERROR_LOAD]);

  const teamMetaById = useMemo(
    () =>
      buildTeamMetaById(
        teams.map((team) => ({
          teamId: team.teamId,
          teamName: team.teamName,
          fullName: team.fullName,
        })),
      ),
    [teams],
  );

  return {
    teams,
    standingsLoading,
    chartData,
    chartTeams,
    chartLoading,
    teamMetaById,
    lastUpdated,
    live,
    error,
  };
};
